/** Every remastered sprite by the same names as the C64 set (`src/render/sprites`). Pure. */

import type { SpriteName } from '../sprites';
import type { RampSet } from './color';
import { amoeba, butterfly, firefly, slime } from './creatures';
import { digbyFront, digbyPalette, digbyRun } from './digby';
import { clock, explosion, key, lockedDoor, openingDoor } from './extras';
import { boulder, door, gem } from './objects';
import type { Px } from './px';
import { brick, cover, dirt, DIRT_TILES, expandingWall, magicWall, steel } from './terrain';

const range = (n: number): number[] => Array.from({ length: n }, (_, i) => i);

/**
 * Frames per sprite. Dirt has sixteen pieces of one seamless texture and brick and steel four
 * variants, which the cave view picks by cell; the
 * rest animate as the C64 set does, frame numbers wrapping.
 */
export function remasterFrames(r: RampSet): Partial<Record<SpriteName, Px[]>> {
  const d = digbyPalette(r.ink);
  const run = range(4).map((f) => digbyRun(d, f));
  return {
    dirt: range(DIRT_TILES).map((v) => dirt(r, v)),
    brick: range(4).map((v) => brick(r, v)),
    steel: range(4).map((v) => steel(r, v)),
    magicWall: range(4).map((k) => magicWall(r, k)),
    boulder: [boulder(r)],
    diamond: range(8).map((k) => gem(r, k)),
    door: [door(r)],
    digbyStand: [digbyFront(d)],
    digbyBlink: [digbyFront(d, { blink: true })],
    digbyTap: [digbyFront(d, { tap: true })],
    digbyRight: run,
    digbyLeft: run.map((f) => f.mirrored()),
    firefly: range(4).map((k) => firefly(r, k)),
    butterfly: range(4).map((k) => butterfly(r, k)),
    amoeba: range(4).map((k) => amoeba(r, k)),
    explosion: range(5).map((s) => explosion(r, s)),
    cover: range(4).map((k) => cover(r, k)),
    expandingWall: [expandingWall(r)],
    slime: range(4).map((k) => slime(r, k)),
    clock: [clock(r, 0), clock(r, 1)],
    key0: [key(0)],
    key1: [key(1)],
    key2: [key(2)],
    door0: [lockedDoor(0)],
    door1: [lockedDoor(1)],
    door2: [lockedDoor(2)],
    doorOpening: [openingDoor(r)],
  };
}
